import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";

import {
  HeartPulse,
  ShieldCheck,
  Stethoscope,
  Award,
  Clock,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  Users,
  Building2,
  GraduationCap,
  Star,
  Calendar as CalendarIcon,
  Anchor,
  Mountain,
  Siren,
  Activity,
  BookOpen,
  Menu,
} from "lucide-react";

const BookingForm = lazy(() =>
  import("@/components/booking-form").then((mod) => ({ default: mod.BookingForm })),
);

const PHOTO = { width: 427, height: 640 } as const;
const NAV_LINKS = [
  { href: "#services", label: "Courses" },
  { href: "#schedule", label: "Schedule" },
  { href: "#why", label: "Why us" },
  { href: "#process", label: "How it works" },
  { href: "#faq", label: "FAQ" },
] as const;

function toFormat(src: string, ext: "avif" | "webp") {
  return src.replace(/\.(?:png|jpe?g|webp|avif)$/i, `.${ext}`);
}

function Photo({
  src,
  alt,
  priority = false,
  className,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  className?: string;
}) {
  const webp = toFormat(src, "webp");
  const avif = toFormat(src, "avif");
  const sizes = priority ? "(min-width: 768px) 42vw, 100vw" : "(min-width: 768px) 25vw, 50vw";
  return (
    <picture>
      <source type="image/avif" srcSet={avif} sizes={sizes} />
      <source type="image/webp" srcSet={webp} sizes={sizes} />
      <img
        src={webp}
        alt={alt}
        width={PHOTO.width}
        height={PHOTO.height}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "low"}
        decoding={priority ? "sync" : "async"}
        sizes={sizes}
        className={className}
      />
    </picture>
  );
}


const SITE = {
  name: "Safety First Winnipeg",
  phone: "(204) 781-3587",
  phoneHref: "tel:+12047813587",
  email: "ckmunnoc@gmail.com",
  area: "Winnipeg, MB",
};

const FAQS: { q: string; a: string }[] = [
  { q: "How long is a CPR / First Aid certification valid?", a: "Red Cross CPR and Standard First Aid certifications are valid for three years from the date of completion." },
  { q: "Do you offer on-site training for workplaces?", a: "Yes — we travel across Winnipeg and surrounding areas to train teams at their location. Group rates apply." },
  { q: "Are courses Manitoba Workplace Safety & Health approved?", a: "All Standard First Aid + CPR/AED courses meet Manitoba WSH requirements and are recognized province-wide." },
  { q: "How do I book a course?", a: "Call (204) 781-3587 or use the contact form below. We'll confirm dates, location and materials within one business day." },
];

export const Route = createFileRoute("/")({
  component: Landing,
  head: () => ({
    meta: [
      { title: "Winnipeg CPR & First Aid Training | Red Cross Certified" },
      {
        name: "description",
        content:
          "Red Cross certified CPR, AED and First Aid training in Winnipeg, MB. 30+ years of experience. Group, workplace and private courses. Book your class today.",
      },
      { name: "keywords", content: "CPR Winnipeg, First Aid Winnipeg, Red Cross training Winnipeg, AED training, workplace safety Manitoba, respiratory training" },
      { name: "author", content: "Safety First Winnipeg" },
      { name: "robots", content: "index,follow,max-image-preview:large" },
      { property: "og:title", content: "Winnipeg CPR & First Aid Training | Red Cross Certified" },
      { property: "og:description", content: "Red Cross certified CPR, AED and First Aid training in Winnipeg with 30+ years of expertise. Book your class today." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_CA" },
      { property: "og:url", content: "https://safetyfirstmb.com/" },
      { property: "og:site_name", content: "Safety First Winnipeg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "geo.region", content: "CA-MB" },
      { name: "geo.placename", content: "Winnipeg" },
    ],
    links: [{ rel: "canonical", href: "https://safetyfirstmb.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://safetyfirstmb.com/#business",
          name: "Safety First Winnipeg",
          description:
            "Red Cross certified CPR, AED, First Aid and Respiratory training serving Winnipeg, MB and surrounding areas. Over 30 years of experience.",
          telephone: "+1-204-781-3587",
          email: "ckmunnoc@gmail.com",
          areaServed: "Winnipeg, MB",
          address: { "@type": "PostalAddress", addressLocality: "Winnipeg", addressRegion: "MB", addressCountry: "CA" },
          priceRange: "$$",
          openingHoursSpecification: [
            { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], opens: "08:00", closes: "17:00" },
          ],
          aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "27" },
          sameAs: [
            "https://www.linkedin.com/in/craig-munnoch-cd-a7a79770/",
            "https://www.yelp.ca/biz/safety-first-winnipeg",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});


type SelectedSession = { course: string; date: string } | null;

function Landing() {
  const [selected, setSelected] = useState<SelectedSession>(null);
  return (
    <main id="main-content" className="min-h-screen bg-background text-foreground antialiased">
      <TopBar />
      <Header />
      <Hero />
      <TrustBar />
      <Services />
      <WhyUs />
      <Process />
      <Audiences />
      <Gallery />
      <Schedule onSelect={(s) => setSelected(s)} />
      <Testimonials />
      <FAQ />
      <CTA selected={selected} />
      <Footer />
    </main>
  );
}


function TopBar() {
  return (
    <div className="ink-gradient text-white text-xs">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2">
        <span className="flex items-center gap-2 opacity-90">
          <MapPin className="h-3.5 w-3.5" /> Serving Winnipeg, MB & surrounding areas
        </span>
        <a href={SITE.phoneHref} className="hidden items-center gap-2 hover:text-primary sm:flex">
          <Phone className="h-3.5 w-3.5" /> {SITE.phone}
        </a>
      </div>
    </div>
  );
}

function Header() {
  return (
    <>
      <div className="bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-6 py-2 text-center text-xs font-semibold uppercase tracking-[0.18em]">
          <ShieldCheck className="h-3.5 w-3.5" />
          Canadian Red Cross Training Partner
        </div>
      </div>
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2" aria-label="Safety First Winnipeg home">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-primary text-primary-foreground">
            <HeartPulse className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg font-medium">Safety First</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Winnipeg CPR Certification</div>
          </div>
        </Link>
        <nav aria-label="Primary" className="hidden items-center gap-8 text-sm font-medium md:flex">
          {NAV_LINKS.map((item) => (
            <a key={item.href} href={item.href} className="hover:text-primary">
              {item.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <details className="relative md:hidden">
            <summary
              aria-label="Open menu"
              className="flex min-h-11 min-w-11 cursor-pointer list-none items-center justify-center rounded-full border border-border [&::-webkit-details-marker]:hidden"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">Open menu</span>
            </summary>
            <ul className="absolute right-0 z-50 mt-2 min-w-48 rounded-xl border border-border bg-background p-2 shadow-lg">
              {NAV_LINKS.map((item) => (
                <li key={item.href}>
                  <a href={item.href} className="block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-accent hover:text-primary">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </details>
          <a
            href="#book"
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-90"
          >
            Book a Class <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </header>
    </>
  );
}

function Hero() {
  return (
    <section className="hero-gradient relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 md:grid-cols-12 md:py-28">
        <div className="md:col-span-6">
          <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
            <span className="pulse-dot" /> Canadian Red Cross Training Partner · 30+ years
          </div>
          <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.15] tracking-tight text-ink md:text-6xl">
            Save a life in <span className="text-primary">Winnipeg.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            Hands-on CPR, AED and First Aid certification courses from a Canadian Red Cross
            Training Partner with three decades of experience. Confidence, certification, and
            real skills — delivered locally.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#book"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-md transition hover:translate-y-[-1px]"
            >
              Reserve your seat <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={SITE.phoneHref}
              className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-background px-6 py-3 text-sm font-semibold text-foreground hover:border-primary hover:text-primary"
            >
              <Phone className="h-4 w-4" /> {SITE.phone}
            </a>
          </div>
          <dl className="mt-10 grid max-w-lg grid-cols-3 gap-6 text-left">
            {[
              { k: "30+", v: "Years teaching" },
              { k: "5.0", v: "Average rating" },
              { k: "100%", v: "Red Cross certified" },
            ].map((s) => (
              <div key={s.v}>
                <dt className="font-display text-3xl font-semibold text-ink">{s.k}</dt>
                <dd className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{s.v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative md:col-span-6">
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-3xl shadow-xl md:aspect-[5/6]">
            <Photo
              src="/gallery/1885680.webp"
              alt="CPR training class in Winnipeg with instructor and students"
              priority
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute -bottom-5 -left-5 hidden max-w-[230px] rounded-2xl border border-border bg-card p-5 shadow-xl md:block">
            <div className="flex items-center gap-2 text-primary">
              <Award className="h-5 w-5" />
              <span className="text-[10px] font-semibold uppercase tracking-widest">Certified</span>
            </div>
            <div className="mt-2 font-display text-lg font-semibold leading-tight text-ink">
              Red Cross Training Partner
            </div>
            <div className="mt-1 text-xs text-muted-foreground">Valid province-wide · 3-year cert</div>
            <div className="mt-2 text-xs font-semibold text-primary">Canadian Red Cross Training Partner</div>

          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    "Canadian Red Cross Training Partner",
    "Manitoba Workplace Approved",
    "WHMIS · AED · BLS Ready",
    "On-site Group Training Available",
  ];
  return (
    <section className="cv-auto border-y border-border bg-secondary text-secondary-foreground">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-10 gap-y-3 px-6 py-5 text-xs uppercase tracking-[0.22em]">
        {items.map((i) => (
          <span key={i} className="flex items-center gap-2 opacity-90">
            <ShieldCheck className="h-3.5 w-3.5 text-primary-light" /> {i}
          </span>
        ))}
      </div>
    </section>
  );
}

const COURSE_CATALOG = [
  { icon: ShieldCheck, title: "Basic First Aid", subtitle: "Emergency First Aid", duration: "1-Day Course", desc: "Essential life-saving skills for the workplace, home or community. Covers CPR Level C and AED use." },
  { icon: HeartPulse, title: "Standard First Aid with CPR Level C", subtitle: "Intermediate First Aid", duration: "2-Day Course", desc: "Comprehensive first aid with CPR Level C and AED — the most requested workplace certification in Manitoba." },
  { icon: Anchor, title: "Marine Basic First Aid", subtitle: "Home Study", duration: "2-Day Course", desc: "Transport Canada recognized first aid for marine crews and recreational mariners." },
  { icon: Anchor, title: "Marine Advanced First Aid", subtitle: "Transport Canada", duration: "5-Day Course", desc: "Advanced shipboard medical care for senior crew on commercial vessels." },
  { icon: Mountain, title: "Remote First Aid", subtitle: "Extended care", duration: "20-Hour Course", desc: "First aid for delayed-help environments — remote sites, industrial, and back-country travel." },
  { icon: Mountain, title: "Wilderness First Aid", subtitle: "Outdoor leaders", duration: "40-Hour Course", desc: "Multi-day expedition medicine for guides, outdoor educators and adventurers." },
  { icon: Siren, title: "First Responder", subtitle: "Pre-hospital care", duration: "40-Hour Course", desc: "Skills for fire, security, industrial responders and community first responders." },
  { icon: Activity, title: "Advanced First Aid Industrial / AFA", subtitle: "Workplace responder", duration: "80-Hour Course", desc: "In-depth assessment and treatment skills for designated workplace responders. Prerequisite: Standard First Aid with CPR Level C." },
  { icon: Stethoscope, title: "Emergency Medical Responder", subtitle: "EMR", duration: "100+ Hours · Homework", desc: "Pre-hospital emergency care certification — a stepping stone to a paramedic career. Prerequisite: Standard First Aid with CPR Level C." },
  { icon: HeartPulse, title: "Basic Life Support (BLS)", subtitle: "Canadian Red Cross", duration: "Healthcare provider", desc: "Red Cross BLS for healthcare providers — high-performance CPR, AED, choking and team resuscitation." },
  { icon: HeartPulse, title: "Basic Life Support (BLS)", subtitle: "Heart & Stroke Foundation", duration: "Healthcare provider", desc: "Heart & Stroke BLS for healthcare providers — high-quality CPR, AED, airway management and team-based resuscitation." },
  { icon: ShieldCheck, title: "Respirator Training & Fit Testing", subtitle: "Respiratory Protection", duration: "2-Year Certification", desc: "Respirator fit testing and training to ensure proper protection in the workplace — 2-year certification." },
];

function Services() {
  return (
    <section id="services" className="cv-auto mx-auto max-w-7xl px-6 py-24">
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">Courses</div>
          <h2 className="mt-3 max-w-2xl font-display text-4xl text-ink md:text-5xl">
            Certifications that <span className="text-primary">stick</span> when seconds matter.
          </h2>
        </div>
        <p className="max-w-md text-muted-foreground">
          From a 1-day Emergency First Aid to a 100+ hour Emergency Medical Responder program — every course is delivered by an experienced Red Cross instructor.
        </p>
      </div>

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {COURSE_CATALOG.map((s) => (
          <article
            key={`${s.title}-${s.subtitle}`}
            className="group relative flex flex-col rounded-2xl border border-border bg-card p-7 transition hover:border-primary hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                <s.icon className="h-5 w-5" />
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium text-foreground/80">
                <Clock className="h-3 w-3 text-primary" /> {s.duration}
              </span>
            </div>
            <h3 className="mt-5 font-display text-xl leading-tight text-ink">{s.title}</h3>
            <div className="mt-1 text-xs font-medium uppercase tracking-wider text-primary">{s.subtitle}</div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            <a href="#schedule" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary">
              See available dates <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </a>
          </article>
        ))}
      </div>
    </section>
  );
}


function WhyUs() {
  const points = [
    { icon: Award, title: "30 years of expertise", desc: "Three decades of training individuals, teams and first responders." },
    { icon: ShieldCheck, title: "Red Cross partner", desc: "Official Canadian Red Cross Training Partner — fully certified curriculum." },
    { icon: Users, title: "Small, hands-on classes", desc: "Personal attention, real manikins, real scenarios — never a lecture hall." },
    { icon: MapPin, title: "On-site in Winnipeg", desc: "We come to your workplace, school or community group across MB." },
  ];
  return (
    <section id="why" className="cv-auto bg-secondary text-secondary-foreground">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-on-ink">Why Safety First</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">
            Trained by someone who's <span className="text-on-ink">actually been there</span>.
          </h2>
          <p className="mt-6 text-white/85">
            Craig Munnoch built Safety First Winnipeg on a simple belief: a calm,
            confident bystander changes outcomes. Every course is engineered around
            that idea — practice until response is muscle memory.
          </p>
          <a href="#book" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90">
            Book a session <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="grid gap-4 md:col-span-7 md:grid-cols-2">
          {points.map((p) => (
            <div key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
              <p.icon className="h-6 w-6 text-on-ink" />
              <h3 className="mt-4 font-display text-xl text-white">{p.title}</h3>
              <p className="mt-2 text-sm text-white/80">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { n: "01", t: "Pick your course", d: "Choose CPR, First Aid or Respiratory — public class or private group." },
    { n: "02", t: "Reserve your seat", d: "Call or message us. We confirm date, location and materials." },
    { n: "03", t: "Train hands-on", d: "Spend a day practicing real scenarios with expert coaching." },
    { n: "04", t: "Get certified", d: "Walk out with your Red Cross certification — valid for 3 years." },
  ];
  return (
    <section id="process" className="cv-auto mx-auto max-w-7xl px-6 py-24">
      <div className="max-w-3xl">
        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">How it works</div>
        <h2 className="mt-3 font-display text-4xl text-ink md:text-5xl">
          Certified in a day. <span className="text-primary">Ready for life.</span>
        </h2>
      </div>
      <ol className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-4">
        {steps.map((s) => (
          <li key={s.n} className="bg-card p-8">
            <div className="font-display text-5xl text-primary/80">{s.n}</div>
            <h3 className="mt-4 font-display text-xl text-ink">{s.t}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Audiences() {
  const aud = [
    { icon: Building2, t: "Workplaces", d: "Meet Manitoba Workplace Safety & Health requirements with on-site group sessions." },
    { icon: GraduationCap, t: "Schools & coaches", d: "Equip teachers, daycare staff and sports coaches with child & infant CPR." },
    { icon: Users, t: "Parents & families", d: "Confidence at home — for babies, toddlers and aging loved ones." },
  ];
  return (
    <section className="cv-auto bg-cream">
      <div className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">Who we train</div>
        <h2 className="mt-3 max-w-2xl font-display text-4xl text-ink md:text-5xl">
          From boardrooms to backyards.
        </h2>
        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {aud.map((a) => (
            <div key={a.t} className="rounded-2xl border border-border bg-background p-8">
              <a.icon className="h-7 w-7 text-primary" />
              <h3 className="mt-4 font-display text-2xl text-ink">{a.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{a.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  const photos = [
    { src: "/gallery/1885681.webp", alt: "Hands-on CPR practice with manikin", span: "row-span-2" },
    { src: "/gallery/1885682.webp", alt: "Group of students learning first aid", span: "" },
    { src: "/gallery/1885684.webp", alt: "Instructor coaching CPR compressions", span: "" },
    { src: "/gallery/1885686.webp", alt: "AED training device demonstration", span: "row-span-2" },
    { src: "/gallery/1885687.webp", alt: "First aid bandaging practice", span: "" },
    { src: "/gallery/1885688.webp", alt: "Winnipeg workplace CPR certification class", span: "" },
  ];
  return (
    <section className="cv-auto border-y border-border bg-background">
      <div className="mx-auto max-w-7xl px-6 py-24">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">From the classroom</div>
            <h2 className="mt-3 max-w-2xl font-display text-4xl text-ink md:text-5xl">
              Real classes. <span className="text-primary">Real Winnipeggers.</span>
            </h2>
          </div>
          <p className="max-w-md text-muted-foreground">
            A look inside recent training sessions — workplaces, families, and first responders building
            life-saving confidence together.
          </p>
        </div>
        <div className="mt-12 grid auto-rows-[180px] grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {photos.map((p, i) => (
            <figure
              key={p.src}
              className={`group relative overflow-hidden rounded-2xl bg-muted ${p.span} ${i === 0 ? "md:col-span-2" : ""}`}
            >
              <Photo
                src={p.src}
                alt={p.alt}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const reviews = [
    {
      q: "Best CPR course I've ever taken. Craig made every scenario click — I left feeling like I could actually help someone.",
      n: "Sarah M.",
      r: "HR Manager, Winnipeg",
    },
    {
      q: "He came on-site to our office and certified our entire team in a day. Professional, calm, and genuinely engaging.",
      n: "David R.",
      r: "Operations Lead",
    },
    {
      q: "30 years of experience really shows. The stories and hands-on practice made every technique stick.",
      n: "Priya K.",
      r: "Daycare Director",
    },
  ];
  return (
    <section className="cv-auto mx-auto max-w-7xl px-6 py-24">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">Reviews</div>
          <h2 className="mt-3 font-display text-4xl text-ink md:text-5xl">Trusted across Winnipeg.</h2>
        </div>
        <div className="hidden items-center gap-1 md:flex">
          {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-primary text-primary" />)}
          <span className="ml-2 text-sm text-muted-foreground">5.0 average</span>
        </div>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {reviews.map((t) => (
          <figure key={t.n} className="flex flex-col rounded-2xl border border-border bg-card p-8">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}
            </div>
            <blockquote className="mt-4 font-display text-xl leading-snug text-ink">"{t.q}"</blockquote>
            <figcaption className="mt-6 text-sm">
              <div className="font-semibold text-foreground">{t.n}</div>
              <div className="text-muted-foreground">{t.r}</div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = FAQS;

  return (
    <section id="faq" className="cv-auto bg-cream">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">FAQ</div>
          <h2 className="mt-3 font-display text-4xl text-ink">Questions, answered.</h2>
          <p className="mt-4 text-muted-foreground">Still wondering? Call us — we love talking through the right course for you.</p>
        </div>
        <dl className="md:col-span-8 md:border-l md:border-border md:pl-12">
          {faqs.map((f, i) => (
            <div key={f.q} className={i > 0 ? "border-t border-border pt-6 mt-6" : ""}>
              <dt className="font-display text-xl text-ink">{f.q}</dt>
              <dd className="mt-2 text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

// Upcoming session schedule — update as new dates are confirmed
const SESSIONS: { date: string; course: string; time: string; seats: number }[] = [
  { date: "2026-07-04", course: "Emergency First Aid (Basic)", time: "8:30 AM – 4:30 PM", seats: 6 },
  { date: "2026-07-11", course: "Standard First Aid (Intermediate)", time: "8:30 AM – 4:30 PM (Day 1 of 2)", seats: 8 },
  { date: "2026-07-18", course: "Basic Life Support (BLS)", time: "9:00 AM – 1:00 PM", seats: 10 },
  { date: "2026-07-25", course: "Wilderness First Aid", time: "8:00 AM – 5:00 PM (Day 1 of 5)", seats: 4 },
  { date: "2026-08-01", course: "Marine Basic First Aid", time: "9:00 AM – 5:00 PM", seats: 6 },
  { date: "2026-08-08", course: "First Responder", time: "8:00 AM – 5:00 PM (Day 1 of 5)", seats: 6 },
  { date: "2026-08-15", course: "Emergency First Aid (Basic)", time: "8:30 AM – 4:30 PM", seats: 8 },
  { date: "2026-08-22", course: "Standard First Aid (Intermediate)", time: "8:30 AM – 4:30 PM (Day 1 of 2)", seats: 8 },
  { date: "2026-08-29", course: "Emergency Medical Responder (EMR)", time: "8:00 AM – 5:00 PM (Module 1)", seats: 4 },
  { date: "2026-09-05", course: "Advanced First Aid", time: "8:00 AM – 5:00 PM (Day 1 of 10)", seats: 6 },
  { date: "2026-09-12", course: "Remote First Aid", time: "8:00 AM – 4:00 PM (Day 1 of 3)", seats: 6 },
  { date: "2026-09-19", course: "Marine Advanced First Aid", time: "8:00 AM – 5:00 PM (Day 1 of 5)", seats: 4 },
  { date: "2026-09-26", course: "Basic Life Support (BLS)", time: "9:00 AM – 1:00 PM", seats: 10 },
];

function parseLocalDate(d: string) {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day);
}

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"] as const;

function firstUpcomingSession() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return SESSIONS.find((s) => parseLocalDate(s.date) >= start) ?? SESSIONS[0];
}

function SessionMonth({
  month,
  onMonthChange,
  selectedKey,
  onSelect,
  sessionKeys,
}: {
  month: Date;
  onMonthChange: (next: Date) => void;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  sessionKeys: Set<string>;
}) {
  const year = month.getFullYear();
  const mo = month.getMonth();
  const startPad = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const cells: Array<number | null> = [
    ...Array.from({ length: startPad }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => onMonthChange(new Date(year, mo - 1, 1))}
          className="grid h-11 w-11 place-items-center rounded-md text-lg text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          ‹
        </button>
        <div className="text-sm font-medium text-foreground">
          {month.toLocaleString("en-CA", { month: "long", year: "numeric" })}
        </div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => onMonthChange(new Date(year, mo + 1, 1))}
          className="grid h-11 w-11 place-items-center rounded-md text-lg text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-[0.8rem] text-muted-foreground" aria-hidden="true">
        {WEEKDAY_LABELS.map((label, i) => (
          <div key={`${label}-${i}`}>{label}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7" aria-label="Class calendar">
        {cells.map((day, i) => {
          if (day == null) return <div key={`e-${i}`} />;
          const key = `${year}-${String(mo + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isSession = sessionKeys.has(key);
          const isSelected = selectedKey === key;
          if (!isSession) {
            return (
              <span key={key} className="grid aspect-square place-items-center text-sm text-muted-foreground">
                {day}
              </span>
            );
          }
          return (
            <button
              key={key}
              type="button"
              aria-pressed={isSelected}
              aria-label={`${parseLocalDate(key).toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })}, class available`}
              onClick={() => onSelect(key)}
              className={
                isSelected
                  ? "relative grid aspect-square place-items-center rounded-md bg-primary text-sm font-semibold text-primary-foreground"
                  : "relative grid aspect-square place-items-center rounded-md text-sm font-semibold text-primary hover:bg-accent"
              }
            >
              {day}
              <span className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Schedule({ onSelect }: { onSelect: (s: { course: string; date: string }) => void }) {
  const sessionKeys = useMemo(() => new Set(SESSIONS.map((s) => s.date)), []);
  const [month, setMonth] = useState(() => parseLocalDate(firstUpcomingSession().date));
  const [pickedKey, setPickedKey] = useState(() => firstUpcomingSession().date);
  const dayMatches = SESSIONS.filter((s) => s.date === pickedKey);

  return (
    <section id="schedule" className="cv-auto bg-cream">
      <div className="mx-auto max-w-7xl px-6 py-24">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">Schedule</div>
            <h2 className="mt-3 max-w-2xl font-display text-4xl text-ink md:text-5xl">
              Upcoming <span className="text-primary">class dates</span>.
            </h2>
            <p className="mt-4 max-w-xl text-muted-foreground">
              Pick a highlighted date to see the session details, then reserve your seat. Need a different date or on-site group training? Just call us.
            </p>
          </div>
          <a href={SITE.phoneHref} className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-primary hover:text-primary">
            <Phone className="h-4 w-4" /> {SITE.phone}
          </a>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <SessionMonth
                month={month}
                onMonthChange={setMonth}
                selectedKey={pickedKey}
                onSelect={setPickedKey}
                sessionKeys={sessionKeys}
              />
              <div className="mt-3 flex items-center gap-2 px-2 text-xs text-muted-foreground">
                <span className="inline-block h-2 w-2 rounded-full bg-primary" /> Available training day
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-background p-2">
              {dayMatches.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
                  <CalendarIcon className="h-8 w-8 text-primary" />
                  <p className="font-display text-lg text-ink">No class on this date</p>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Select a highlighted day, or scroll through the next sessions below.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {dayMatches.map((s) => (
                    <li key={s.course + s.date} className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-primary">
                          {parseLocalDate(s.date).toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                        </div>
                        <div className="mt-1 font-display text-xl text-ink">{s.course}</div>
                        <div className="mt-1 text-sm text-muted-foreground">{s.time} · {s.seats} seats remaining</div>
                      </div>
                      <a
                        href="#book"
                        onClick={() => onSelect({ course: s.course, date: s.date })}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
                      >
                        Book this date <ArrowRight className="h-4 w-4" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                <BookOpen className="h-3.5 w-3.5" /> Next sessions
              </div>
              <ul className="grid gap-2 sm:grid-cols-2">
                {SESSIONS.slice(0, 6).map((s) => (
                  <li key={s.date + s.course}>
                    <button
                      type="button"
                      onClick={() => {
                        setPickedKey(s.date);
                        setMonth(parseLocalDate(s.date));
                      }}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left text-sm transition hover:border-primary"
                    >
                      <span className="font-medium text-foreground">{s.course}</span>
                      <span className="text-xs text-muted-foreground">
                        {parseLocalDate(s.date).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA({ selected }: { selected: SelectedSession }) {
  const preset = selected?.course ?? "";
  const presetDate = selected?.date ?? "";
  const presetDateLabel = presetDate
    ? `Selected: ${parseLocalDate(presetDate).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" })}`
    : "";
  const defaultMessage = presetDate
    ? `I'd like to book the ${preset} session on ${presetDate}.`
    : "";
  const courses = COURSE_CATALOG.map((c) => ({ title: c.title, duration: c.duration }));

  return (
    <section id="book" className="cv-auto relative overflow-hidden ink-gradient text-white">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-primary-light">Book your class</div>
          <h2 className="mt-3 font-display text-4xl md:text-6xl">
            The skill you hope to never use.
            <br />
            <span className="text-primary-light">Learn it anyway.</span>
          </h2>
          <p className="mt-6 max-w-md text-white/85">
            Public classes weekly. Private group sessions on your schedule. Reach out and
            we'll confirm within one business day.
          </p>
          <div className="mt-8 space-y-4 text-sm">
            <a href={SITE.phoneHref} className="flex items-center gap-3 hover:text-primary-light">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/15 text-primary-light">
                <Phone className="h-4 w-4" />
              </span>
              <span className="font-semibold tracking-wide">{SITE.phone}</span>
            </a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-primary-light">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/15 text-primary-light">
                <Mail className="h-4 w-4" />
              </span>
              <span className="font-semibold tracking-wide">{SITE.email}</span>
            </a>
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/15 text-primary-light">
                <Clock className="h-4 w-4" />
              </span>
              <span className="text-white/80">Mon–Sat · 8:00 AM – 5:00 PM</span>
            </div>
          </div>
        </div>

        <DeferredBookingForm
          preset={preset}
          presetDateLabel={presetDateLabel}
          defaultMessage={defaultMessage}
          courses={courses}
          phone={SITE.phone}
          email={SITE.email}
        />
      </div>
    </section>
  );
}

function BookingFormPlaceholder() {
  return (
    <div className="min-h-[28rem] rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur">
      <h3 className="font-display text-2xl">Request a booking</h3>
      <p className="mt-1 text-sm text-white/80">We'll get back within one business day.</p>
    </div>
  );
}

function DeferredBookingForm({
  preset,
  presetDateLabel,
  defaultMessage,
  courses,
  phone,
  email,
}: {
  preset: string;
  presetDateLabel: string;
  defaultMessage: string;
  courses: { title: string; duration: string }[];
  phone: string;
  email: string;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = document.getElementById("book");
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setReady(true);
          io.disconnect();
        }
      },
      { rootMargin: "240px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  if (!ready) return <BookingFormPlaceholder />;

  return (
    <Suspense fallback={<BookingFormPlaceholder />}>
      <BookingForm
        preset={preset}
        presetDateLabel={presetDateLabel}
        defaultMessage={defaultMessage}
        courses={courses}
        phone={phone}
        email={email}
      />
    </Suspense>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-md bg-primary text-primary-foreground">
              <HeartPulse className="h-5 w-5" />
            </div>
            <div className="font-display text-lg">Safety First Winnipeg</div>
          </div>
          <p className="mt-4 max-w-sm text-sm text-muted-foreground">
            Red Cross certified CPR, First Aid and Respiratory training. Serving Winnipeg, MB and surrounding areas for over 30 years.
          </p>
        </div>
        <div className="text-sm">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Contact</div>
          <ul className="mt-4 space-y-2">
            <li><a href={SITE.phoneHref} className="hover:text-primary">{SITE.phone}</a></li>
            <li><a href={`mailto:${SITE.email}`} className="hover:text-primary">{SITE.email}</a></li>
            <li className="text-muted-foreground">{SITE.area}</li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Hours</div>
          <ul className="mt-4 space-y-1 text-muted-foreground">
            <li>Mon – Sat · 8:00 AM – 5:00 PM</li>
            <li>Sun · Closed</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-6 py-5 text-xs text-muted-foreground md:flex-row">
          <span>© {new Date().getFullYear()} Safety First Winnipeg. All rights reserved.</span>
          <span>Canadian Red Cross Training Partner</span>
        </div>
      </div>
    </footer>
  );
}
